import type { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';

import { AccountRoutes } from '@webapp/account/src/resources/routes';
import type { States } from '@webapp/account/src/resources/states';

import { Alert } from '@webapp/common/components/message';
import { API_URL } from '@webapp/common/conf';
import { ApiRespCode, isSubdomain } from '@webapp/common/lib/api';
import {
    getCookie,
    getShareToken,
    getSurveyTokenCookie,
    getUserToken,
    setCookieWithSubdomains,
} from '@webapp/common/lib/cookies';
import { getFormattedParams, ttFetch } from '@webapp/common/lib/fetch';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';

const badToken = new Set([ApiRespCode.ERR_TOKEN_CODE, ApiRespCode.ERR_BAD_TOKEN]);

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

export const getCanaryKey = (userId: number): `${number}_account_is_canary` => `${userId}_account_is_canary`;

export const checkCanary = async (userId: number): Promise<boolean> => {
    // Check if we already have a valid canary flag for this user
    const existingCanary = LocalStorage.get<boolean>(getCanaryKey(userId));
    if (existingCanary !== null) {
        if (existingCanary) {
            const cookieValue = getCookie('is_canary');
            if (!cookieValue) {
                setCookieWithSubdomains('is_canary', 'always', {
                    expires: dayjs().add(1, 'day').toDate(),
                });
                setTimeout(() => window.location.reload(), 100);
            }
        }
        return existingCanary;
    }

    try {
        const token = getUserToken();
        const response = await fetch(`${API_URL}/core/v1/check/canary`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                accept: 'application/json',
            },
            body: JSON.stringify({ token }),
        });

        if (!response.ok) {
            return false;
        }

        const data = await response.json();
        const isCanary = Boolean(data.isCanary);

        // Save userId and canary flag
        LocalStorage.set(LocalStorageKeys.ACCOUNT_USER_ID, userId);
        LocalStorage.set(getCanaryKey(userId), isCanary, ONE_DAY_IN_SECONDS);

        if (isCanary) {
            setCookieWithSubdomains('is_canary', 'always', {
                expires: dayjs().add(1, 'day').toDate(),
            });
            setTimeout(() => window.location.reload(), 100);
        }

        return isCanary;
    } catch (e) {
        // Silently fail - canary check is not critical
        console.error('Canary check failed:', e);
        return false;
    }
};

const goAuth = (reason: string): void => {
    // TODO redirect with action
    const { pathname } = window.location;
    const isAuth = pathname.startsWith(AccountRoutes.auth.root);

    if (pathname === AccountRoutes.unsubscribe.link) return;

    if (!isAuth || pathname === AccountRoutes.authConfirmDeviceRequest.link) {
        if (!isAuth) {
            LocalStorage.set(
                LocalStorageKeys.BACK_URL,
                `backUrl=${encodeURIComponent(pathname)}` + (reason ? `&reason=${encodeURIComponent(reason)}` : ''),
            );
        }
        AccountRoutes.authLogin.assign();
    }
};

const successCallback = (response: Record<string, any>) => {
    if (typeof response !== 'object') {
        console.error('successCallback invalid response type:', response);
        return Promise.reject(response);
    }
    const { code, message, status } = response;
    // todo
    if (code === undefined || code === ApiRespCode.ERR_STATUS_CODE || code === ApiRespCode.OK_CODE) {
        return Promise.resolve(response);
    }

    if (status === 401 || badToken.has(code)) {
        return Promise.reject(goAuth(response.url.includes('get/profile') ? '' : message));
    }

    return Promise.reject(response);
};

export const makeRequest = ({
    method,
    body,
    headers,
    path,
    params,
    absPath,
    callback,
}: {
    method: string;
    path: string;
    headers?: Record<string, string>;
    body?: any;
    params?: Record<string, any>;
    absPath?: boolean;
    callback?: (resp: Record<string, any>) => void;
}): Promise<any> => {
    const url = absPath ? `${API_URL}${path}` : `${API_URL}/core/v1/${path}`; // TODO purge,check start/results

    const formattedParams = getFormattedParams(params);

    return ttFetch({
        method,
        credentials: 'include',
        retries: 1,
        headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
        },
    })(`${url}${formattedParams}`, {
        headers,
        body,
    }).then(callback || successCallback);
};

export const POST = (
    path: string,
    data: Record<string, any> = {},
    config?: {
        absPath?: boolean;
        callback?: (resp: Record<string, any>) => void;
        params?: Record<string, any>;
        withoutToken?: boolean;
        json?: boolean;
    },
): Promise<any> => {
    const token = getUserToken();
    const shareToken = getShareToken();
    let body;

    data = cloneDeep(data) || {};

    if (token && !config?.withoutToken) {
        data['token'] = token;
    }

    if (shareToken && !config?.withoutToken) {
        data['shareToken'] = shareToken;
    }

    if (config?.json) {
        body = data;
    } else {
        body = new FormData();
        for (const [key, val] of Object.entries(data)) {
            const value = val instanceof File ? val : isObject(val) ? JSON.stringify(val) : (val as string);

            if (value !== undefined) {
                body.append(key, value);
            }
        }
    }

    return makeRequest({
        method: 'POST',
        body,
        path,
        params: config?.params,
        absPath: config?.absPath,
        callback: config?.callback,
        headers: {
            ...(isSubdomain && { 'X-Authorization': getSurveyTokenCookie() }),
        },
    });
};

export const DELETE = (path: string, absPath = false): Promise<any> => {
    const token = getUserToken();

    return makeRequest({
        method: 'DELETE',
        path,
        absPath,
        headers: {
            'T-Authorization': token,
        },
    });
};

export const GET = (
    path: string,
    params?: Record<string, any>,
    config?: {
        absPath?: boolean;
        callback?: (resp: Record<string, any>) => void;
    },
): Promise<any> => {
    const token = getUserToken();
    const shareToken = getShareToken();

    const newParams = params || {};

    if (shareToken) {
        newParams['shareToken'] = shareToken;
    }

    return makeRequest({
        method: 'GET',
        path,
        params: newParams,
        absPath: config?.absPath,
        headers: {
            'T-Authorization': token || '',
        },
    });
};

export const patchOrPut =
    (method: string) =>
    (path: string, data: Record<string, any> = {}, params?: Record<string, any>, absPath = false): Promise<any> => {
        const token = getUserToken();
        data = cloneDeep(data) || {};

        if (token) {
            data['token'] = token;
        }

        const body = new FormData();

        for (const [key, val] of Object.entries(data)) {
            const value = val instanceof File ? val : isObject(val) ? JSON.stringify(val) : (val as string);

            if (value !== undefined) {
                body.append(key, value);
            }
        }

        return makeRequest({
            method,
            body,
            path,
            params,
            absPath,
            headers: {
                ...(isSubdomain && { 'X-Authorization': getSurveyTokenCookie() }),
                'T-Authorization': token,
            },
        });
    };

export const PATCH = patchOrPut('PATCH');
export const PUT = patchOrPut('PUT');

export const wrapApiCall =
    <Args, Returned = AnyObject, ThunkApiConfig = unknown>(
        payloadCreator: AsyncThunkPayloadCreator<
            Returned & { message?: string },
            Args,
            ThunkApiConfig & { state: States }
        >,
        {
            notify = true,
            notifyError = true,
            type,
        }: { notify?: boolean | string; notifyError?: boolean | string; type?: string } = {},
    ) =>
    async (args, thunkAPI) => {
        const requestId = type || thunkAPI.requestId;
        notify && Alert.saving(undefined, requestId);

        try {
            const resp: any = await payloadCreator(args, thunkAPI);

            if (notify) {
                const message = typeof notify === 'string' ? notify : resp.message;
                Alert.success(message, requestId);
            }
            return resp;
        } catch (e) {
            const { code, status } = e;
            let { message } = e;

            // Объекты-ответы API уже логируются в fetch.ts (стабильные сообщения
            // по endpoint/статусу); console.error(e) давал бесполезную группу
            // "[object Object]" на каждый фейл запроса (FRONT-2JD).
            if (e instanceof Error) {
                console.error(e);
            }

            if (notifyError) {
                message = typeof notifyError === 'string' ? notifyError : message;
                Alert.error(message, requestId);
            }
            return thunkAPI.rejectWithValue({ code, message, status });
        }
    };

export const isRespSuccess = (resp: Record<string, any>) => resp?.payload?.status === 200;

export const createThunkRequest = (
    type: string,
    func,
    options?: { notify?: boolean | string; notifyError?: boolean | string; type?: string },
) => {
    return createAsyncThunk(type, wrapApiCall(func, { ...options, type }));
};
